import { faBars, faCalendar, faCheck, faChevronRight, faMinus, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { ReactNode, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { makeNav } from "../mirror/Footer";
import { isExpansionLocale } from "../mirror/landing_page_expansion";
import { Img } from "./Img";
import { Locale, LocaleLanguage, languageSwitch, locPath } from "./Languages";
import { LinkAsButton, useGetCurrentSearch } from "./LinkAsButton";
import { TrackedExternalLink, TrackedLink, TrackedLinkAsButton } from "./TrackedLink";

const useLocalStorageFlag = (key: string, initial?: boolean): [boolean, (state: boolean) => void] => {
    const [flag, _setFlag] = useState(false);
    useEffect(() => {
        _setFlag(localStorage.getItem(key) != null ? localStorage.getItem(key) === "true" : !!initial);
    });
    const setFlag = (state: boolean) => {
        _setFlag(state);
        localStorage.setItem(key, state.toString());
    };
    return [flag, setFlag];
};

export const Navbar = ({ locale, hc }: { locale: Locale; hc?: boolean }) => {
    const [open, setOpen] = useState(false);

    const NavbarCheck = (props: { title: string; subtitle?: string }) => {
        return (
            <div className="flex gap-2 justify-start shrink-0  text-zinc-400 group-hover/navitem:text-zinc-500 text-xs font-normal">
                <div className="mt-[3px] shrink-0 w-3.5 h-3.5 bg-zinc-300 group-hover/navitem:bg-zinc-800 text-white rounded-full flex items-center justify-center text-[8px]">
                    <FontAwesomeIcon icon={faCheck} />
                </div>
                <div className="">
                    <div>{props.title}</div>
                </div>
            </div>
        );
    };

    let items: {
        name: string;
        children: {
            href: string;
            title: string;
            text: string;
            icon?: ReactNode;
            content?: ReactNode;
            banner?: ReactNode;
        }[];
    }[] = [];

    const localeIsDACH = locale === "DE-DE" || locale === "DE-AT" || locale === "DE-CH";

    if (localeIsDACH) {
        // navbar for DACH
        items = [
            //
            {
                name: "Produkte",
                children: [
                    {
                        href: locPath(locale, "/product/360h/"),
                        title: "360°-Haushalt",
                        text: "All-In-One Software für Photovoltaik, Speicher, Wallbox & Wärmepume. Von erstem Kundenkontakt bis zur Installation.",
                        icon: <Img className="w-10" size="lg" alt="360h-icon" src="/shared/icon_energiehaus.svg" />,
                        content: (
                            <div className="mt-3 flex flex-col gap-1">
                                <NavbarCheck title="Photovoltaik, Speicher, Wallbox & Wärmepumpe" />
                                <NavbarCheck title="Projektmanagement" />
                                <NavbarCheck title="Projektmanagement inklusive" />
                            </div>
                        ),
                    },
                    {
                        href: locPath(locale, "/product/360b/"),
                        title: "360°-Gewerbekunden",
                        text: "All-In-One Software für Photovoltaik, Speicher, Ladeinftrastruktur & mehr. Von erstem Kundenkontakt bis zur Installation.",
                        icon: <Img className="w-10" size="lg" alt="360b-icon" src="/shared/icon_energiefirma.svg" />,
                        content: (
                            <div className="mt-3 flex flex-col gap-1">
                                <NavbarCheck title="Photovoltaik, Speicher, Wallbox & Wärmepumpe" />
                                <NavbarCheck title="Projektmanagement" />
                                <NavbarCheck title="Projektmanagement inklusive" />
                            </div>
                        ),
                    },
                ],
            },
            //
            {
                name: "Lösungen",
                children: [
                    {
                        href: locPath(locale, "/industry/solar/"),
                        title: "Für Solarteure",
                        text: "Entdecke unsere Lösungen für Vertrieb, Planung & Installation.",
                    },
                    {
                        href: locPath(locale, "/industry/solar-b2b/"),
                        title: "Für Solarteure (Gewerbe)",
                        text: "Entdecke unsere Lösungen für Vertrieb & Planung.",
                    },
                    {
                        href: locPath(locale, "/industry/utilities/"),
                        title: "Für Stadtwerke",
                        text: "Entdecke unsere Lösungen für Energiedienstleistungen.",
                    },
                    {
                        href: locPath(locale, "/industry/heating/"),
                        title: "Für Heizungsbauer",
                        text: "Entdecke unsere Lösungen für Vertrieb, Planung & Installation.",
                        banner: "Coming soon",
                    },
                ],
            },
            {
                name: "Wissen",
                children: [
                    {
                        href: locPath(locale, "/blog/"), // TODO
                        title: "Blog",
                        text: "Lies unseren Blog.",
                    },
                    // {
                    //     href: locPath(locale, "/about/team/"), // TODO
                    //     title: "Knowledgebase",
                    //     text: "Lerne unser Team kennen.",
                    // },
                ],
            },
            {
                name: "Über uns",
                children: [
                    {
                        href: locPath(locale, "/about/us/"),
                        title: "Über uns",
                        text: "Lerne uns, unseren Ansatz und unsere Unterstützer kennen.",
                    },
                    {
                        href: locPath(locale, "/about/team/"),
                        title: "Team",
                        text: "Lerne unser Team kennen.",
                    },
                    {
                        href: locPath(locale, "/about/jobs/"),
                        title: "Offene Stellen",
                        text: "Auf Jobsuche? Hier entlang.",
                        banner: "We are hiring",
                    },
                ],
            },
        ];
        // navbar for UK/US
    } else if (locale === "EN-GB") {
        items = [
            //
            // {
            //     name: "Products",
            //     children: [
            //         {
            //             href: locPath("EN-GB", "/product/360h/"),
            //             title: "360°-Household",
            //             text: "All-In-One Software for Solar, Storage, Wallboxes & Heatpumps. Everything from first customer contact to installation.",
            //             icon: (
            //                 <Img
            //                     className="w-10"
            //                     size="lg"
            //                     alt=""
            //                     src="/shared/icon_energiehaus.svg"
            //                 />
            //             ),
            //             content: (
            //                 <div className="mt-3 flex flex-col gap-1">
            //                     <NavbarCheck title="Photovoltaik, Speicher, Wallbox & Wärmepumpe" />
            //                     <NavbarCheck title="Projektmanagement" />
            //                     <NavbarCheck title="Projektmanagement inklusive" />
            //                 </div>
            //             ),
            //         },
            //         // {
            //         //     href: locPath("EN-GB", "/product/360b/"),
            //         //     title: "360°-Business",
            //         //     text: "All-In-One Software for Solar, Storage, Charing Infrastructure & more. Everything from first customer contact to installation.",
            //         //     icon: (
            //         //         <Img
            //         //             className="w-10"
            //         //             size="lg"
            //         //             alt=""
            //         //             src="/shared/icon_energiefirma.svg"
            //         //         />
            //         //     ),
            //         //     content: (
            //         //         <div className="mt-3 flex flex-col gap-1">
            //         //             <NavbarCheck title="Photovoltaik, Speicher, Wallbox & Wärmepumpe" />
            //         //             <NavbarCheck title="Projektmanagement" />
            //         //             <NavbarCheck title="Projektmanagement inklusive" />
            //         //         </div>
            //         //     ),
            //         // },
            //     ],
            // },
            //
            {
                name: "Solutions",
                children: [
                    {
                        href: locPath("EN-GB", "/industry/solar/"),
                        title: "For solar installers",
                        text: "Discover our solutions for sales and planning.",
                    },
                ],
            },
            {
                name: "About us",
                children: [
                    {
                        href: locPath("EN-GB", "/about/us/"),
                        title: "About us",
                        text: "Learn more about us, our approach and our supporters.",
                    },
                    {
                        href: locPath("EN-GB", "/about/team/"),
                        title: "Team",
                        text: "Get to know our team.",
                    },
                    {
                        href: locPath("EN-GB", "/about/jobs/"),
                        title: "Open positions",
                        text: "Looking for a new job? You're in the right place here.",
                        banner: "We are hiring",
                    },
                ],
            },
        ];
    }
    // navbar for IT/ES/SV/PT/PTBR/CZ/PL
    else if (!isExpansionLocale(locale)) {
        const localLanguage = LocaleLanguage[locale];
        items = [
            {
                name: languageSwitch(localLanguage, {
                    DE: "Lösungen",
                    EN: "Solutions",
                    SV: "Lösningar",
                    PTBR: "Soluções",
                    PT: "Soluções",
                    CZ: "Řešení",
                    ES: "Soluciones",
                    FR: "Solutions",
                    IT: "Soluzioni",
                    PL: "Rozwiązania",
                    RO: "Soluții",
                    NL: "Oplossingen",
                }),
                children: [
                    {
                        href: locPath(locale, "/industry/solar/"),
                        title: languageSwitch(localLanguage, {
                            DE: "Für Solateure",
                            EN: "For solar installers",
                            SV: "För solcellsinstallatörer",
                            PTBR: "Para instaladores de energia solar",
                            PT: "Para instaladores de sistemas fotovoltaicos",
                            CZ: "Pro instalatéry solárních zařízení",
                            ES: "Para instaladores solares",
                            FR: "Pour les installateurs solaires",
                            IT: "Per gli installatori solari",
                            PL: "Dla instalatorów systemów solarnych",
                            RO: "Pentru instalatorii de sisteme solare",
                            NL: "Voor zonne-installateurs",
                        }),
                        text: languageSwitch(localLanguage, {
                            DE: "Entdecke unsere Lösungen für Vertrieb & Planung.",
                            EN: "Discover our solutions for sales and planning.",
                            SV: "Upptäck våra lösningar för försäljning och planering.",
                            PTBR: "Descubra nossas soluções para vendas e planejamento.",
                            PT: "Descubra as nossas soluções para vendas e planeamento",
                            CZ: "Objevte naše řešení pro prodej a plánování.",
                            ES: "Descubra nuestras soluciones para ventas y planificación.",
                            FR: "Découvrez nos solutions pour la vente et la planification.",
                            IT: "Scoprite le nostre soluzioni per le vendite e la pianificazione.",
                            PL: "Odkryj nasze rozwiązania dla sprzedaży i planowania.",
                            RO: "Descoperiți soluțiile noastre pentru vânzări și planificare.",
                            NL: "Ontdek onze oplossingen voor verkoop en planning.",
                        }),
                    },
                ],
            },
            {
                name: languageSwitch(localLanguage, {
                    DE: "Über uns",
                    EN: "About us",
                    SV: "Om oss",
                    PTBR: "Sobre nós",
                    PT: "Sobre nós",
                    CZ: "O nás",
                    ES: "Quiénes somos",
                    FR: "À propos de nous",
                    IT: "Chi siamo",
                    PL: "O nas",
                    RO: "Despre noi",
                    NL: "Over ons",
                }),
                children: [
                    {
                        href: locPath(locale, "/about/us/"),
                        title: languageSwitch(localLanguage, {
                            DE: "Über uns",
                            EN: "About us",
                            SV: "Om oss",
                            PTBR: "Sobre nós",
                            PT: "Sobre nós",
                            CZ: "O nás",
                            ES: "Quiénes somos",
                            FR: "À propos de nous",
                            IT: "Chi siamo",
                            PL: "O nas",
                            RO: "Despre noi",
                            NL: "Over ons",
                        }),
                        text: languageSwitch(localLanguage, {
                            DE: "Lerne unseren Ansatz und unsere Unterstützer kennen",
                            EN: "Get to know our approach and our supporters",
                            SV: "Lär känna vår strategi och våra supportrar",
                            PTBR: "Conheça nossa abordagem e nossos apoiadores",
                            PT: "Conheça a nossa abordagem e os nossos apoiantes",
                            CZ: "Seznamte se s naším přístupem a našimi příznivci",
                            ES: "Conozca nuestro enfoque y a quienes nos apoyan",
                            FR: "Découvre notre approche et nos soutiens",
                            IT: "Conosci il nostro approccio e i nostri partner",
                            PL: "Poznaj nasze podejście i naszych zwolenników",
                            RO: "Faceți cunoștință cu abordarea noastră și cu susținătorii noștri",
                            NL: "Leer onze aanpak en onze supporters kennen",
                        }),
                    },
                    {
                        href: locPath(locale, "/about/team/"),
                        title: languageSwitch(localLanguage, {
                            DE: "Team",
                            EN: "Team",
                            SV: "Team",
                            PTBR: "Equipe",
                            PT: "Equipa",
                            CZ: "Tým",
                            ES: "Equipo",
                            FR: "L'équipe",
                            IT: "Team",
                            PL: "Zespół",
                            RO: "Echipa",
                            NL: "Team",
                        }),
                        text: languageSwitch(localLanguage, {
                            DE: "Lerne unser Team kennen.",
                            EN: "Get to know our team.",
                            SV: "Lär känna vårt team.",
                            PTBR: "Conheça a nossa equipe.",
                            PT: "Conheça a nossa equipa",
                            CZ: "Seznamte se s naším týmem.",
                            ES: "Conozca a nuestro equipo.",
                            FR: "Rencontre notre équipe.",
                            IT: "Conosci la nostra squadra",
                            PL: "Poznaj nasz zespół.",
                            RO: "Faceți cunoștință cu echipa noastră.",
                            NL: "Leer ons team kennen.",
                        }),
                    },
                    {
                        href: locPath(locale, "/about/jobs/"),
                        title: languageSwitch(localLanguage, {
                            DE: "Offene Stellen",
                            EN: "Career",
                            SV: "Karriär",
                            PTBR: "Carreira",
                            PT: "Carreira",
                            CZ: "Volná místa",
                            ES: "Vacantes",
                            FR: "Postes à pourvoir",
                            IT: "Offerte di lavoro",
                            PL: "Wakaty",
                            RO: "Carieră",
                            NL: "Vacatures",
                        }),
                        text: languageSwitch(localLanguage, {
                            DE: "Auf Jobsuche? Hier entlang.",
                            EN: "Looking for a job? This way.",
                            SV: "Letar du efter ett jobb? Den här vägen.",
                            PTBR: "Procurando um emprego? Por aqui.",
                            PT: "À procura de emprego? Por aqui",
                            CZ: "Hledáte práci? Takto.",
                            ES: "¿Buscas trabajo? Por aquí.",
                            FR: "A la recherche d'un emploi ? Par ici.",
                            IT: "Posizioni aperte",
                            PL: "Szukasz pracy? W ten sposób.",
                            RO: "Căutați un loc de muncă? Pe aici.",
                            NL: "Op zoek naar een baan? Hier langs.",
                        }),
                    },
                ],
            },
        ];
    }

    const { ref, inView } = useInView({
        initialInView: true,
    });
    const [anyIsHovering, setAnyIsHovering] = useState<number | undefined>();

    const [banner, showBanner] = useLocalStorageFlag(`show-banner-open-positions-3-${new Date().getUTCDate()}`, true);

    const search = useGetCurrentSearch();

    // TODO: expansion pages do not have a proper navbar yet
    if (isExpansionLocale(locale)) {
        return <></>;
    }
    const language = LocaleLanguage[locale];

    return (
        <>
            <div className="print:hidden" ref={ref} />
            <div
                className={clsx(
                    "w-full backdrop-blur-lg  group/navbar border-b -mb-[55px] z-50 fixed top-0 left-0",
                    "transition-all",
                    inView && hc ? "bg-gray-100/75" : "bg-white/75",
                )}
            >
                {locale === "DE-DE" && (
                    <div className="w-full bg-gray-100 py-1.5 text-black font-medium">
                        <div className="mx-auto max-w-[1300px] px-8 text-sm flex flex-col md:flex-row gap-0.5 justify-between md:items-center">
                            <div className="flex-col gap-0.5 hidden md:flex">
                                <div>Besuchen Sie uns auf der Intersolar in München!</div>
                                <div className="text-xs hidden md:block">
                                    📍 Stand A4.214 &middot; München &middot; 7-9. Mai 2025
                                </div>
                            </div>
                            <a
                                className="text-white hover:text-white/80 bg-reonic-blue px-4 h-fit py-1 rounded-full text-center flex items-center justify-center"
                                href="https://demodesk.com/book/reonic-team/intersolar-2025"
                            >
                                Zur Terminbuchung Intersolar
                            </a>
                        </div>
                    </div>
                )}
                <div
                    className={
                        "mx-auto max-w-[1300px] text-sm font-medium grow flex flex-col items-center px-5 lg:px-10"
                    }
                >
                    <div className="w-full flex items-center h-14 shrink-0">
                        <TrackedLink to={makeNav(locale, search)} className="flex items-center justify-start my-auto">
                            <Img size="lg" className="w-[74px]" alt="reonic logo" src={"/images/logo.svg"} />
                        </TrackedLink>
                        <div className="ml-auto" />

                        <div className="hidden md:flex h-full">
                            {items.map((m, i) => (
                                <motion.div
                                    key={i}
                                    whileHover="hover"
                                    onClick={() => setAnyIsHovering(i)}
                                    onMouseEnter={(e) => setAnyIsHovering(i)}
                                    onMouseLeave={(e) => setAnyIsHovering(undefined)}
                                    className="flex items-center px-2 h-full group/item cursor-pointer"
                                >
                                    <div className={`rounded-lg transition-colors`}>
                                        <div className="px-2 rounded-lg font-medium">
                                            {m.name}
                                            <FontAwesomeIcon
                                                className={`ml-2 group-hover/item:rotate-90 transition-all text-xs`}
                                                icon={faChevronRight}
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        {/* {locale === "DE-DE" && (
                            <a
                                href="https://wa.me/4915560717708"
                                target="_blank"
                                className="rounded-full bg-green-500 text-white flex justify-center items-center gap-2 h-8 w-8"
                                rel="noreferrer"
                            >
                                <FontAwesomeIcon icon={faWhatsapp} className="text-lg" />
                            </a>
                        )} */}
                        <div className="ml-2 hidden md:block">
                            <LinkAsButton
                                to={locPath(locale, "/book-a-meeting/")}
                                className={"bg-gray-100 text-zinc-800 hover:bg-gray-100/50"}
                            >
                                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                                {languageSwitch(language, {
                                    DE: "Demo buchen",
                                    EN: "Get a demo",
                                    SV: "Boka demo",
                                    PTBR: "Agendar uma demonstração",
                                    PT: "Pedir demonstração",
                                    PL: "Umów demo",
                                    ES: "Solicite una demo",
                                    CZ: "Získejte ukázku",
                                    FR: "Demander une démo",
                                    IT: "Richiedi una demo",
                                    RO: "Obțineți o demonstrație",
                                    NL: "Neem een demo",
                                })}
                            </LinkAsButton>
                        </div>
                        <div className="ml-2 hidden md:block">
                            <TrackedExternalLink
                                href={"https://portal.reonic.de"}
                                className="p-1.5 px-4 rounded-full font-medium cursor-pointer border border-gray-200 shrink-0 bg-zinc-800 text-white hover:bg-zinc-700"
                            >
                                Login
                            </TrackedExternalLink>
                        </div>
                        <TrackedLinkAsButton
                            onClick={() => setOpen(!open)}
                            className="bg-gray-100 text-zinc-800 hover:bg-gray-100/50
                            text-xs rounded-full cursor-pointer shrink-0 md:hidden ml-1"
                            to={locPath(locale, "/book-a-meeting/")}
                        >
                            <FontAwesomeIcon icon={faCalendar} className="mr-1.5" />
                            {languageSwitch(language, {
                                DE: "Demo buchen",
                                EN: "Demo",
                                SV: "Demo",
                                PTBR: "Demo",
                                PT: "Demo",
                                PL: "Demo",
                                ES: "Demo",
                                CZ: "Demo",
                                FR: "Démo",
                                IT: "Demo",
                                RO: "Demonstrație",
                                NL: "Demo",
                            })}
                        </TrackedLinkAsButton>
                        {/* Mobile hamburger menu */}
                        <button
                            className="ml-1 shrink-0 rounded-full text-sm h-[32px] aspect-square flex items-center justify-center md:hidden
                             bg-white group-[.is-scrolled]:bg-transparent transition-all
                            "
                            onClick={() => setOpen(!open)}
                            name="menu"
                            aria-label="menu"
                        >
                            {!open && <FontAwesomeIcon fixedWidth icon={faBars} />}
                            {open && <FontAwesomeIcon fixedWidth icon={faTimes} />}
                        </button>
                    </div>
                </div>

                {/* Navbar Desktop */}
                <AnimatePresence>
                    {anyIsHovering !== undefined && (
                        <div
                            // initial={{ maxHeight: "0px" }}
                            // animate={{ maxHeight: "500px" }}
                            // exit={{ maxHeight: "0px" }}
                            // transition={{ duration: 0.3 }}
                            onMouseOver={(e) => setAnyIsHovering(anyIsHovering)}
                            onMouseLeave={(e) => setAnyIsHovering(undefined)}
                            className="overflow-hidden w-full flex flex-col mx-auto max-w-[1300px] px-3 lg:px-10 pt-10 pb-14"
                        >
                            {/* <div className="text-sm font-semibold mb-5">{items[anyIsHovering].name}</div> */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
                                {items[anyIsHovering].children.map((c, i) => (
                                    <TrackedLink
                                        key={i}
                                        to={c.href ?? ""}
                                        className="group/navitem flex gap-2 items-start"
                                    >
                                        {/* {c.icon && <div className="mt-1.5">{c.icon}</div>} */}
                                        <div className="flex flex-col">
                                            <span className="font-bold text-lg leading-5 group-hover/navitem:text-black/65">
                                                {c.title}
                                            </span>
                                            <div className="mt-1 text-xs text-zinc-500 group-hover/navitem:text-zinc-500/80 font-normal leading-[16px]">
                                                {c.text}
                                            </div>
                                        </div>
                                    </TrackedLink>
                                ))}
                            </div>
                        </div>
                    )}
                </AnimatePresence>

                {/* Navbar Mobile */}
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ height: 0, opacity: 1 }}
                            animate={{
                                height: "auto",
                                opacity: 1,
                            }}
                            transition={{
                                type: "tween",
                            }}
                            exit={{ height: 0, opacity: 1 }}
                            className="
                                    print:hidden md:hidden w-full flex justify-center shadow-lg 
                                    grow overflow-auto max-h-[90svh]
                                "
                        >
                            <div className="w-full flex flex-col max-w-[1300px] px-6 pt-2 pb-4 gap-2">
                                <TrackedExternalLink
                                    href={"https://portal.reonic.de"}
                                    className="p-1.5 px-4 rounded-full font-medium cursor-pointer border border-gray-200 hover:bg-gray-200 shrink-0"
                                >
                                    Login
                                </TrackedExternalLink>
                                <LinkAsButton
                                    onClick={() => setOpen(!open)}
                                    className="
                                            p-1.5 px-4 rounded-full bg-reonic-blue text-white cursor-pointer w-full flex 
                                            justify-between border border-gray-200 hover:bg-gray-200 shrink-0
                                        "
                                    to={locPath(locale, "/book-a-meeting/")}
                                >
                                    {languageSwitch(language, {
                                        DE: "Demo buchen",
                                        EN: "Get a demo",
                                        SV: "Boka demo",
                                        PTBR: "Agendar uma demonstração",
                                        PT: "Pedir demonstração",
                                        PL: "Umów demo",
                                        ES: "Solicite una demo",
                                        CZ: "Získejte ukázku",
                                        FR: "Demander une démo",
                                        IT: "Richiedi una demo",
                                        RO: "Obțineți o demonstrație",
                                        NL: "Neem een demo",
                                    })}
                                </LinkAsButton>
                                <div className="border-b w-full py-2 shrink-0" />
                                {items.map((m, i) => (
                                    <div
                                        className="font-bold border-b last-of-type:border-0 pb-4 mb-2 last-of-type:border-b-0 last-of-type:mb-10 shrink-0"
                                        key={i}
                                    >
                                        <div className="mb-1 text-sm text-neutral-500">{m.name}</div>
                                        <div className="flex flex-col gap-3 mt-1">
                                            {m.children.map((c, i) => (
                                                <TrackedLink to={c.href} key={i} className="text-sm font-medium">
                                                    {c.title}
                                                    <div className="text-[8pt] leading-[11pt] -mt-[0pt] font-normal text-gray-500">
                                                        {c.text}
                                                    </div>
                                                </TrackedLink>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

export const Arrow = ({
    twText = "text-[9pt]",
    twWidth = "w-3",
    flip,
}: {
    twText?: string;
    twWidth?: string;
    flip?: boolean;
}) => {
    return (
        <div
            className={`overflow-hidden flex items-center ${twWidth} ${flip ? "mr-2 -ml-1 rotate-180" : "ml-2 -mr-1"}`}
        >
            <FontAwesomeIcon
                fixedWidth
                className={`transition-all -ml-[13px] group-hover/arrow:-ml-1 ${twText}`}
                icon={faMinus}
            />
            <FontAwesomeIcon
                fixedWidth
                className={`transition-all -ml-[7px] group-hover/arrow:-ml-[10.3px] ${twText}`}
                icon={faChevronRight}
            />
        </div>
    );
};
